<template>
  <div class="dui-gallery">
    <vue-gallery
      :id="`gallery-${ _uid }`"
      :images="vm.imagesList"
      :index="vm.galleryIndex"
      @close="closePerformed"
      @onslide="slidePerformed"
    />

    <!-- Pages -->
    <div
      v-if="showPages"
      class="pages-container"
    >
      <!-- Empty -->
      <DuiGalleryVessel
        v-if="(isEditable && !vm.imagesList.length)"
        @add-image="addImage"
      />
      <!-- END Empty -->

      <!-- Page images -->
      <div
        v-if="vm.imagesList.length"
        class="pages"
        :style="{ transform: 'translateX(' + pagesTranslateX + 'px)' }"
      >
        <DuiGalleryThumbnail
          v-for="(image, idx) in vm.imagesList"
          :key="image"
          :image="image"
          :is-page="true"
          @click="openGallery(idx)"
        />
      </div>

      <div class="controls">
        <button
          v-if="vm.imagesList.length"
          class="button-black button-gallery"
          @click="openGallery()"
        >
          View as gallery
        </button>

        <nav>
          <button
            class="button-black button-icon"
            :disabled="isLeftDisabled"
            type="button"
            @click="leftPerformed"
          >
            <DuiIconArrowLeft class="icon-circle background-black-base icon-white" />
          </button>

          <button
            class="button-black button-icon space-left-1em"
            :disabled="isRightDisabled"
            type="button"
            @click="rightPerformed"
          >
            <DuiIconArrowRight class="icon-circle background-black-base icon-white" />
          </button>
        </nav>
      </div>
      <!-- END Page images -->
    </div>
    <!-- END Pages -->

    <!-- Grid -->
    <div>
      <VueDraggable
        v-if="isEditable"
        v-model="vm.imagesList"
        class="thumbnail-grid"
        draggable=".dui-gallery-thumbnail.draggable"
        @end="dragEnded"
        @start="dragStarted"
      >
        <DuiGalleryThumbnail
          v-for="(image, idx) in vm.imagesList"
          :key="image"
          class="draggable"
          :class="{ 'highlight': (showPages && vm.selectedIndex === idx) }"
          :image="image"
          :is-editable="isEditable"
          @click="imageSelected(idx)"
          @remove="removeImage(idx)"
        />
        <a
          class="dui-gallery-thumbnail add-more"
          title="Add image"
          @click="addImage"
        >
          <DuiIconPlus class="icon-lg icon-circle background-yellow-base icon-white" />
        </a>
      </VueDraggable>

      <div
        v-if="!isEditable"
        class="thumbnail-grid"
      >
        <DuiGalleryThumbnail
          v-for="(image, idx) in vm.imagesList"
          :key="image"
          :class="{ 'highlight': (showPages && vm.selectedIndex === idx) }"
          :image="image"
          @click="imageSelected(idx)"
        />
      </div>
    </div>
    <!-- END Grid -->
  </div>
</template>

<script>
import VueDraggable from 'vuedraggable';
import VueGallery from 'vue-gallery';

import DuiGalleryThumbnail from './dui-gallery-thumbnail/dui-gallery-thumbnail.vue';
import DuiGalleryVessel from './dui-gallery-vessel/dui-gallery-vessel.vue';

// vue-gallery has a bug in which the library
// checks for window.blueimp.Gallery which will throw
window.blueimp = window.blueimp || {};

const DRAGGING_CLASS = 'dragging';
const PAGE_MARGIN = 16;
const PAGE_THUMBS_SELECTOR = '.pages-container .pages .dui-gallery-thumbnail';

export default {
  components: {
    DuiGalleryThumbnail,
    DuiGalleryVessel,
    VueDraggable,
    VueGallery,
  },
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    showPages: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    vm: {
      galleryIndex: null,
      imagesList: [],
      selectedIndex: 0,
    },
  }),
  computed: {
    isLeftDisabled() {
      return (this.vm.selectedIndex === 0);
    },
    isRightDisabled() {
      return (this.vm.selectedIndex >= (this.vm.imagesList.length - 1));
    },
    pagesTranslateX() {
      if (!this.showPages || !this.vm.selectedIndex) {
        return 0;
      }

      try {
        const thumbnailEls = this.$el.querySelectorAll(PAGE_THUMBS_SELECTOR);
        let xTranslation = 0;

        for (let i=0; i<this.vm.selectedIndex; i++) {
          xTranslation -= (thumbnailEls[i].clientWidth + PAGE_MARGIN);
        }

        return xTranslation;
      } catch (e) {
        return 0;
      }
    },
  },
  watch: {
    images: function() {
      this.updateImagesList();
    },
  },
  mounted() {
    this.updateImagesList();
  },
  methods: {
    addImage() {
      this.$emit('add-image');
    },

    closePerformed() {
      this.$set(this.vm, 'galleryIndex', null);
    },

    dragEnded(evt) {
      this.$el.classList.remove(DRAGGING_CLASS);
      this.$set(this.vm, 'selectedIndex', evt.newIndex);
      this.$emit('order-updated', this.vm.imagesList);
    },

    dragStarted() {
      this.$el.classList.add(DRAGGING_CLASS);
    },

    imageSelected(galleryIndex) {
      if (this.showPages) {
        this.$set(this.vm, 'selectedIndex', galleryIndex);
        return;
      }

      this.openGallery(galleryIndex);
    },

    leftPerformed() {
      if (this.vm.selectedIndex <= 0) {
        return;
      }

      this.$set(this.vm, 'selectedIndex', this.vm.selectedIndex - 1);
    },

    openGallery(galleryIndex = this.vm.selectedIndex) {
      this.$set(this.vm, 'galleryIndex', galleryIndex);
    },

    removeImage(galleryIndex) {
      this.$emit('remove-image', this.vm.imagesList[galleryIndex]);
    },

    rightPerformed() {
      if (this.vm.selectedIndex >= (this.vm.imagesList.length - 1)) {
        return;
      }

      this.$set(this.vm, 'selectedIndex', this.vm.selectedIndex + 1);
    },

    slidePerformed(slideEvt) {
      if (slideEvt.index === this.vm.selectedIndex) {
        return;
      }

      this.$set(this.vm, 'selectedIndex', slideEvt.index);
    },

    updateImagesList() {
      this.$set(this.vm, 'imagesList', this.images);
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.dui-gallery {
  box-sizing: border-box;
  display: grid;
  width: 100%;

  .thumbnail-grid {
    margin: 0 (-$_2em) 0 (-$border-width-lg);
    width: calc(100% + #{ $_2em });

    .dui-gallery-thumbnail,
    .add-more {
      display: inline-block;
      vertical-align: middle;
    }

    .dui-gallery-thumbnail {
      margin: 0 ($_2em - $border-width-lg) $_1em 0;
      width: calc(25% - #{ $_2em - $border-width-lg });

      @include screenMoreThan(xs) {
        width: auto;

        .thumbnail {
          max-height: $thumbnail-size-xs;
          max-width: $thumbnail-size-xs;
        }

      }
    }

    .add-more {
      background-color: getColor($grey, medium);
      border: $border-width-lg solid getColor($white);
      height: $thumbnail-size-xs;

      width: calc(25% - #{ $_2em - $border-width-lg });

      @include screenMoreThan(xs) {
        width: $thumbnail-size-xs;
      }

      @include transition((
        border-color
      ));

      &:hover {
        border-color: getColor($yellow) !important;
      }

      .dui-icon {
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate3d(-50%, -50%, 0);
      }
    }
  }

  .pages-container {
    margin: 0 (-$_2em);
    overflow: hidden;
    position: relative;

    @include screenMoreThanCustom($gallery-page-width) {
      &:after {
        background-image: linear-gradient(to left, getColor($white) $_1em, $color-transparent-white);
        content: " ";
        height: 100%;
        pointer-events: none;
        position: absolute;
        right: 0;
        top: 0;
        transform: translate3d(0,0,0);
        width: $_4em;
        z-index: 1;
      }
    }

    .pages {
      align-items: center;
      display: flex;
      padding: 0 $_4em $_1em $_2em;
      overflow: visible;

      @include transition((
        transform
      ));

      .dui-gallery-thumbnail {
        box-shadow: $box-shadow-sm;
        flex: none;
        margin: 0 0 $_2em $_2em;

        @include screenLessThanCustom($gallery-page-width) {
          max-width: calc(100% + #{ $_2em} );
        }

        &:first-child {
          margin-left: 0;
        }

        .thumbnail {
          border: none;

          @include screenMoreThanCustom($gallery-page-width) {
            max-height: $gallery-page-height;
            max-width: $gallery-page-width;
          }
        }
      }
    }

    .controls {
      .button-gallery,
      nav {
        bottom: $_5em;
        position: absolute;
        transform: translate3d(0,0,0);
        z-index: 2;
      }

      .button-gallery {
        left: $_4em;
      }

      nav {
        right: $_4em;
      }
    }
  }

  &.background-grey {
    .thumbnail-grid {
      .dui-gallery-thumbnail .thumbnail,
      .add-more {
        border-color: getColor($grey);
      }
    }

    .pages-container:after {
      background-image: linear-gradient(to left, getColor($grey) $_2em, $color-transparent-grey);
    }

    &.dragging {
      .dui-gallery-thumbnail.editable:hover {
        .thumbnail {
          border-color: getColor($grey) !important;
        }
      }
    }
  }

  &.dragging {
    .dui-gallery-thumbnail.editable {
      &.sortable-ghost {
        opacity: $disabled-opacity;

        .thumbnail {
          border-color: getColor($yellow) !important;
        }
      }

      &:hover {
        .thumbnail {
          border-color: getColor($white) !important;
        }

        .button-remove {
          opacity: 0;
          pointer-events: none;
        }
      }
    }
  }
}
</style>
